import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  EditFormMode,
  IIncidentByRideListRowDto,
  IncidentsApi,
  IncidentStatusEnum,
  IRideDto,
  isISODateStr,
  isValidDateTime,
  RideCrudModeKeyTuple,
  rideIncidentsTabCountsAF,
  simpleResultErrorEmptyResponse,
  TIncidentStatus,
  useDrawers,
  useFormats,
  useHasPermissions,
  useSystemParams,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import {
  incidentUnboundCrudKey,
  type TRideIncidentStatusFilter,
} from "./incident-tab.models";
import {
  buildIncidentDraftFromRide,
  getIncidentCreatedAtMsForSort,
} from "./ride-incidents-tab.utils";

export const useRideTabIncidents = () => {
  const { t } = useTranslation();
  const { formatDateTimeNormalized } = useFormats();
  const rideDto = useRecoilValue(editFormDataAF(RideCrudModeKeyTuple)) as
    IRideDto | undefined;
  const { openEditFormDrawer } = useDrawers();
  const setIncidentFormDto = useSetRecoilState(
    editFormDataAF(incidentUnboundCrudKey),
  );
  const setIncidentFormSnapshot = useSetRecoilState(
    editFormDataInitialSnapshotAF(incidentUnboundCrudKey),
  );

  const { hasPermissions: canRead } = useHasPermissions({
    entity: "incident",
    action: "read",
  });
  const { hasPermissions: canCreate } = useHasPermissions({
    entity: "incident",
    action: "create",
  });
  const { hasPermissions: canUpdate } = useHasPermissions({
    entity: "incident",
    action: "update",
  });
  const { hasPermissions: canUpdateStatus } = useHasPermissions({
    entity: "incident",
    action: "update_status",
  });
  const { enableIncidents } = useSystemParams();
  const isIncidentsEnabled = Boolean(enableIncidents);

  const [statusFilter, setStatusFilter] = useState(
    "all" as TRideIncidentStatusFilter,
  );
  const [allRows, setAllRows] = useState([] as IIncidentByRideListRowDto[]);
  const [loadingList, setLoadingList] = useState(false);

  const rideId = rideDto?.id ?? 0;

  const setIncidentsTabCounts = useSetRecoilState(
    rideIncidentsTabCountsAF(rideId),
  );

  const formatIncidentCreatedAt = useCallback(
    (createdAt: TDateISO | null | undefined): string => {
      if (!createdAt || !isValidDateTime(createdAt)) {
        return "";
      }
      // expect: profile date+time format (same as grids)
      return isISODateStr(createdAt)
        ? formatDateTimeNormalized(createdAt, "")
        : "";
    },
    [formatDateTimeNormalized],
  );

  const fetchIncidents = useEvent(async () => {
    if (!rideId || !canRead || !isIncidentsEnabled) {
      setAllRows([]);
      setIncidentsTabCounts(null);
      return;
    }

    setIncidentsTabCounts(null);
    setAllRows([]);
    setLoadingList(true);

    try {
      const res = await IncidentsApi.fetchIncidentsByRide({
        rideId,
      });

      if (!res?.success) {
        warnAndToastSimpleResult(res ?? simpleResultErrorEmptyResponse(t), t);
        setAllRows([]);
        return;
      }

      setAllRows(res.data ?? []);
    } finally {
      setLoadingList(false);
    }
  });

  useEffect(() => {
    void fetchIncidents();
  }, [fetchIncidents, rideId, canRead, isIncidentsEnabled]);

  const displayRows = useMemo(() => {
    const filtered =
      statusFilter === "all"
        ? allRows
        : allRows.filter(
            row => (row.status as TIncidentStatus | undefined) === statusFilter,
          );

    return [...filtered].sort(
      (a, b) =>
        getIncidentCreatedAtMsForSort(b) - getIncidentCreatedAtMsForSort(a),
    );
  }, [allRows, statusFilter]);

  useEffect(() => {
    if (!rideId || !canRead || !isIncidentsEnabled) {
      setIncidentsTabCounts(null);
      return;
    }
    const total = allRows.length;
    const newCount = allRows.filter(
      row => row.status === IncidentStatusEnum.New,
    ).length;
    setIncidentsTabCounts({ newCount, total });
  }, [allRows, canRead, isIncidentsEnabled, rideId, setIncidentsTabCounts]);

  const onEditIncident = useEvent(async (id: number) => {
    await openEditFormDrawer({
      entity: "incident",
      id,
      onCloseAction: () => void fetchIncidents(),
    });
  });

  const onCloseIncident = useEvent(async (id: number) => {
    if (!canUpdateStatus) {
      return;
    }
    const res = await IncidentsApi.closeIncident(id);

    if (!res?.success) {
      warnAndToastSimpleResult(res ?? simpleResultErrorEmptyResponse(t), t);
      return;
    }

    void fetchIncidents();
  });

  const prefetchIncidentCreateDraft = useEvent(() => {
    if (!rideDto?.id) {
      return;
    }

    const draft = buildIncidentDraftFromRide(rideDto);
    setIncidentFormDto(draft);
    setIncidentFormSnapshot(draft);
  });

  /** Opens stacked incident edit-form drawer (create); await finishes when drawer resolves. */
  const openIncidentCreateDrawer = useEvent(async () => {
    if (!rideDto?.id || !canCreate) {
      return;
    }

    prefetchIncidentCreateDraft();

    const result = await openEditFormDrawer({
      entity: "incident",
      id: undefined,
      mode: EditFormMode.UnboundDrawer,
      onCloseAction: () => void fetchIncidents(),
    });

    if (result.success && result.data && typeof result.data.id === "number") {
      setStatusFilter("all");
    }
  });

  return {
    t,
    rideDto,
    canRead,
    canCreate,
    canUpdate,
    canUpdateStatus,
    statusFilter,
    setStatusFilter,
    loadingList,
    displayRows,
    onEditIncident,
    onCloseIncident,
    formatIncidentCreatedAt,
    openIncidentCreateDrawer,
    refreshIncidents: fetchIncidents,
  };
};
